import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import nautilusApi from '../nautilusApi'
import useSearchParams from './useSearchParams'

// Key attached to redirect state before replacing away the `source` param from
// the URL. By passing a state flag along with replace/push we can choose to
// not respond to history events that are created in this way.
//
// The practical use case: this is how the app can know to log a "topnav" source
// search event, but not to immediately log the followup history.replace that
// removes the `source` from the query (which cleans the query for future
// interactions without a specific source).
//
const sourceStateKey = 'sourced'

const api = {
  async trackView(location) {
    const body = {
      path: location.pathname + location.search,
    }

    nautilusApi
      .fetch('/views', {
        method: 'POST',
        body: JSON.stringify(body),
        // The forceAuthorization option triggers the API to throw if a token
        // isn't available, rather than trying to make unauthenticated requests.
        // Currently at least, our page views are only tracked for logged in
        // users.
        forceAuthorization: true,
      })
      .catch(e => {
        if (e.message !== 'No Content') {
          console.warn('Unable to log view', body)
        }
      })
  }
}

export default function useViewTracking() {
  const history = useHistory()
  const [search, updateSearch] = useSearchParams()

  // Replace `source`d paths, instead of pushing, to avoid triggering
  // a new tracked view
  useEffect(() => {
    if (!search.source) { return }
    updateSearch(
      { ...search, source: undefined },
      { replace: true, [sourceStateKey]: true }
    )
  }, [search, updateSearch])

  // On initial mount and on push requests, track a new view
  useEffect(() => {
    api.trackView(history.location, 'ENTRY')

    const unlisten = history.listen((location, action) => {
      if (location.state?.[sourceStateKey]) {
        return
      }

      api.trackView(location, action)
    })

    return () => unlisten()
  }, [history])
}
